import NextLink from 'next/link';

import AppQrCode from '@components/homepage/app-qr-code';
import HeroVideo from '@components/homepage/hero-video';
import PressLogos from '@components/homepage/press-logos';
import StoreBadges from '@components/homepage/store-badges';
import TrustpilotRating from '@components/quiz/shared/TrustpilotRating';
import { AnalyticsEvents, captureAnalyticsEvent } from '@lib/analytics/events';
import { formatRatingValue } from '@lib/app-store-rating';
import type { AppStoreRating } from '@lib/app-store-rating';
import type { MarketingCopyHomepage } from '@lib/sanity';

const HERO_PLACEMENT = 'homepage_hero';
// Micro Star's natural height; fixed so the widget can't shift the hero.
const TRUSTPILOT_HEIGHT = '24px';
const TRUSTPILOT_WIDTH = '100%';

type HomepageHeroProps = {
  copy: MarketingCopyHomepage;
  appStoreRating: AppStoreRating | null;
};

const AppStoreRatingLine = ({ rating }: { rating: AppStoreRating }) => (
  <p className="font-open-sans text-xs text-landing-slate">
    <span className="font-semibold text-landing-ink">App Store</span>{' '}
    <span aria-hidden="true" className="text-warning-600">
      ★
    </span>{' '}
    {`${formatRatingValue(
      rating.ratingValue
    )} from ${rating.ratingCount.toLocaleString('en-GB')} ratings`}
  </p>
);

/**
 * The logged-out front door: one free action, the app, and proof. Marketing
 * owns the headline, subline, button text and button link in Sanity; the
 * rest is fixed so the page's structure stays what search engines index.
 */
const HomepageHero = ({ copy, appStoreRating }: HomepageHeroProps) => (
  <section
    aria-labelledby="homepage-hero-heading"
    className="bg-landing-surface"
  >
    <div className="mx-auto grid max-w-7xl gap-10 px-4 pt-8 pb-12 md:px-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:items-center lg:gap-14 lg:pt-14 lg:pb-16">
      <div className="max-w-[620px]">
        <p className="inline-flex rounded-full bg-primary-50 px-3 py-1 font-open-sans text-[11px] font-bold uppercase tracking-[.12em] text-primary-800">
          Free · 10 seconds · No card
        </p>
        <h1
          id="homepage-hero-heading"
          className="mt-4 font-sans text-[34px] font-extrabold leading-[1.08] tracking-[-.02em] text-landing-ink [text-wrap:balance] md:text-5xl"
        >
          {copy.title}
        </h1>
        <p className="mt-4 max-w-[540px] font-open-sans text-base leading-7 text-landing-slate md:text-lg">
          {copy.subtitle}
        </p>
        <NextLink
          href={copy.ctaHref}
          className="mt-6 inline-flex w-full items-center justify-center rounded-lg bg-primary-700 px-6 py-3.5 font-sans text-base font-bold text-white transition-colors hover:bg-primary-800 sm:w-auto"
          data-cy="homepage-hero-cta"
          onClick={() =>
            captureAnalyticsEvent(AnalyticsEvents.homepageCtaClicked, {
              placement: HERO_PLACEMENT,
              href: copy.ctaHref
            })
          }
        >
          {copy.buttonText}
        </NextLink>

        <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-4">
          <StoreBadges placement={HERO_PLACEMENT} />
          <AppQrCode placement={HERO_PLACEMENT} caption="Scan to get the app" />
        </div>

        <div className="mt-6 space-y-2 border-t border-landing-hairline pt-5">
          <div className="h-6 max-w-[320px]">
            <TrustpilotRating
              height={TRUSTPILOT_HEIGHT}
              width={TRUSTPILOT_WIDTH}
            />
          </div>
          {appStoreRating && <AppStoreRatingLine rating={appStoreRating} />}
          <PressLogos className="pt-1" />
        </div>
      </div>

      <HeroVideo asset={copy.asset} />
    </div>
  </section>
);

export default HomepageHero;
